import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Paper } from '@c2fo/liquidity';
import { getToken } from '../../utils/token';
import { createInsightsApiClient } from '../../lib/apiClient';
import { fetchUsersByAuthServiceUuids, type User } from '../../services/userService';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface IssueReportRecord {
  id: number;
  thread_id: string;
  message_id: string;
  auth_service_uuid: string | null;
  issue_type: string;
  description: string;
  created_at: string;
}

interface IssueReportResponse {
  reports: IssueReportRecord[];
}

interface FeedbackStats {
  unique_users: number;
  unique_threads: number;
  anonymous_feedback: number;
  latest_feedback_at: string | null;
  active_users_7d: number;
  active_users_30d: number;
}

export const InsightsFeedbackPage = () => {
  const insightsClient = useMemo(() => createInsightsApiClient(), []);
  const [reports, setReports] = useState<IssueReportRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userEmails, setUserEmails] = useState<Record<string, string | null>>({});
  const [isUserLookupLoading, setIsUserLookupLoading] = useState<boolean>(false);
  const [userLookupError, setUserLookupError] = useState<string | null>(null);
  const userEmailsRef = useRef<Record<string, string | null>>({});

  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  userEmailsRef.current = userEmails;

  const formatDate = (value: string): string => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  };

  const buildThreadUrl = (threadId: string): string => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/insights?threadId=${encodeURIComponent(threadId)}`;
  };

  const getUserEmailDisplay = (authServiceUuid: string | null): string => {
    if (!authServiceUuid) {
      return 'N/A';
    }

    if (!UUID_REGEX.test(authServiceUuid)) {
      return 'Invalid UUID';
    }

    const hasLookup: boolean = Object.prototype.hasOwnProperty.call(userEmails, authServiceUuid);

    if (hasLookup) {
      return userEmails[authServiceUuid] ?? 'Not found';
    }

    return isUserLookupLoading ? 'Loading...' : 'Not found';
  };

  const fetchReports = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await insightsClient
          .get('insights/report-issues', {
            searchParams: { limit: 200 },
            signal,
          })
          .json<IssueReportResponse>();

        setReports(Array.isArray(data.reports) ? data.reports : []);
      } catch (err) {
        if (signal?.aborted) {
          return;
        }

        setError(err instanceof Error ? err.message : 'Failed to load issue reports');
      } finally {
        if (!signal?.aborted) {
          setIsLoading(false);
        }
      }
    },
    [insightsClient],
  );

  useEffect(() => {
    const abortController = new AbortController();
    void fetchReports(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [fetchReports]);

  const fetchStats = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      setIsStatsLoading(true);
      setStatsError(null);

      try {
        const data = await insightsClient.get('insights/feedback/stats', { signal }).json<FeedbackStats>();

        setStats(data);
      } catch (err) {
        if (signal?.aborted) {
          return;
        }

        setStatsError(err instanceof Error ? err.message : 'Failed to load feedback stats');
      } finally {
        if (!signal?.aborted) {
          setIsStatsLoading(false);
        }
      }
    },
    [insightsClient],
  );

  useEffect(() => {
    const abortController = new AbortController();
    void fetchStats(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [fetchStats]);

  useEffect(() => {
    if (reports.length === 0) {
      return;
    }

    const rawUuids: string[] = reports
      .map((report: IssueReportRecord) => report.auth_service_uuid)
      .filter((uuid): uuid is string => typeof uuid === 'string' && uuid.trim().length > 0);
    const uniqueUuids: string[] = Array.from(new Set(rawUuids));
    const validUuids: string[] = uniqueUuids.filter((uuid) => UUID_REGEX.test(uuid));
    const uuidsToFetch: string[] = validUuids.filter(
      (uuid) => !Object.prototype.hasOwnProperty.call(userEmailsRef.current, uuid),
    );

    if (uuidsToFetch.length === 0) {
      return;
    }

    const abortController = new AbortController();

    const fetchUsers = async (): Promise<void> => {
      setIsUserLookupLoading(true);
      setUserLookupError(null);

      try {
        const token: string | null = getToken();
        if (!token) {
          throw new Error('Authentication token not found');
        }

        const users: User[] = await fetchUsersByAuthServiceUuids(uuidsToFetch, token, abortController.signal);
        if (abortController.signal.aborted) {
          return;
        }

        setUserEmails((prev: Record<string, string | null>) => {
          const next: Record<string, string | null> = { ...prev };

          for (const uuid of uuidsToFetch) {
            if (!Object.prototype.hasOwnProperty.call(next, uuid)) {
              next[uuid] = null;
            }
          }

          for (const user of users) {
            if (user.authServiceUuid) {
              next[user.authServiceUuid] = user.emailAddress ?? null;
            }
          }

          return next;
        });
      } catch (err) {
        if (!abortController.signal.aborted) {
          setUserLookupError(err instanceof Error ? err.message : 'Failed to load user details');
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsUserLookupLoading(false);
        }
      }
    };

    void fetchUsers();

    return () => {
      abortController.abort();
    };
  }, [reports]);

  return (
    <>
      <Paper className="p-6 w-full max-w-[1800px] mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Insights Feedback</h1>
            <p className="text-sm text-gray-600 mt-1">All issue reports submitted for Insights threads.</p>
          </div>
        </div>

        <div className="text-sm text-gray-600">
          {reports.length} report{reports.length === 1 ? '' : 's'}
        </div>
      </Paper>

      {isStatsLoading && (
        <div className="p-6 text-center text-gray-600 max-w-[1800px] mx-auto">Loading feedback stats...</div>
      )}

      {statsError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 mb-6 max-w-[1800px] mx-auto">
          {statsError}
        </div>
      )}

      {!isStatsLoading && !statsError && stats && (
        <Paper className="p-6 w-full max-w-[1800px] mx-auto mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Feedback Statistics</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-700">{stats.unique_users}</div>
              <div className="text-sm text-green-600">Unique Users</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-700">{stats.unique_threads}</div>
              <div className="text-sm text-purple-600">Unique Threads</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-700">{stats.active_users_7d}</div>
              <div className="text-sm text-orange-600">Active Users (7d)</div>
            </div>
            <div className="bg-teal-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-teal-700">{stats.active_users_30d}</div>
              <div className="text-sm text-teal-600">Active Users (30d)</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-lg font-bold text-gray-700">
                {stats.latest_feedback_at ? formatDate(stats.latest_feedback_at) : 'N/A'}
              </div>
              <div className="text-sm text-gray-600">Latest Feedback</div>
            </div>
          </div>
        </Paper>
      )}

      {isLoading && <div className="p-6 text-center text-gray-600">Loading issue reports...</div>}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 mb-6 max-w-[1800px] mx-auto">
          {error}
        </div>
      )}

      {!error && userLookupError && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 mb-6 max-w-[1800px] mx-auto">
          {userLookupError}
        </div>
      )}

      {!isLoading && !error && reports.length === 0 && (
        <div className="p-6 text-center text-gray-600 max-w-[1800px] mx-auto">No issue reports submitted yet.</div>
      )}

      {!isLoading && !error && reports.length > 0 && (
        <Paper className="p-6 w-full max-w-[1800px] mx-auto mt-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white text-sm">
              <thead>
                <tr className="border-b-2 border-gray-300 bg-gray-50">
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">ID</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">Thread ID</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">Message ID</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">User Email</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">Issue Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">Description</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">Created At</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">Open Thread</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{report.id}</td>
                    <td className="px-4 py-3 font-mono text-xs">{report.thread_id}</td>
                    <td className="px-4 py-3 font-mono text-xs">{report.message_id}</td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      <div>{getUserEmailDisplay(report.auth_service_uuid)}</div>
                      {report.auth_service_uuid && (
                        <div className="font-mono text-[10px] text-gray-500">{report.auth_service_uuid}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold uppercase text-gray-700">{report.issue_type}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-pre-wrap">{report.description}</td>
                    <td className="px-4 py-3 text-xs text-gray-700">{formatDate(report.created_at)}</td>
                    <td className="px-4 py-3 text-xs">
                      <a
                        href={buildThreadUrl(report.thread_id)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Open
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}
    </>
  );
};
